// CO-21 — the standalone Assign/Share sheet, raised OUTSIDE the composer so
// a coach/athlete can re-share an EXISTING program or plan straight from its
// Library card (the smoke gap: "per assegnare o condividere un piano devo
// crearne uno nuovo, quelli esistenti non posso farci nulla"). A SCOUT
// verified ComposerAssigneesBand (PE-48) is already 8 pure-data props + two
// onChange, so it lives here UNCLONED — this file is only its PageSheet
// chrome, the same split Maxes/EditMaxSheet uses: presentational body, the
// host (LibraryBrowser) owns the draft state and the PATCH.
import { useTranslation } from "react-i18next";
import { ScrollView, StyleSheet, Text } from "react-native";

import { PageSheet } from "../primitives/PageSheet";
import type { ProgramApiFetch } from "../ProgramEditor/types";
import type { UiTheme } from "../theme";
import { ComposerAssigneesBand } from "./ComposerAssigneesBand";
import type { AuthoredAs } from "./types";

export type AssignSheetProps = {
  theme: UiTheme;
  visible: boolean;
  /** The item's own name — the sheet's title, same convention as
   *  Maxes/EditMaxSheet's `exerciseName`. */
  title: string;
  apiFetch: ProgramApiFetch;
  /** Picks the i18n namespace for both this chrome's Cancel/Save and the
   *  band's own copy — "program" or "plans" (ComposerAssigneesBand's own
   *  `kind`, both namespaces already carry cancel/save). */
  kind: "program" | "plans";
  authoredAs?: AuthoredAs;
  callerSub: string | null;
  athleteRoster: ReadonlyArray<string>;
  assignedTo: readonly string[];
  assigneeEmails: readonly string[];
  onChangeAssignedTo: (next: string[]) => void;
  onChangeAssigneeEmails: (next: string[]) => void;
  /** True while the host's PATCH is in flight. */
  busy: boolean;
  /** Host-resolved, already-localized save failure — e.g. the typo'd-email
   *  / unregistered-email cases (program.errCode.*) surfaced in the sheet
   *  itself, never swallowed (PE-48 paid for that once already). Null when
   *  there's nothing to show. */
  error: string | null;
  onCancel: () => void;
  onSave: () => void;
  /** Safe-area insets (px) — 0 on web, same contract as PageSheet/EditMaxSheet. */
  topInset?: number;
  bottomInset?: number;
};

export function AssignSheet({
  theme,
  visible,
  title,
  apiFetch,
  kind,
  authoredAs,
  callerSub,
  athleteRoster,
  assignedTo,
  assigneeEmails,
  onChangeAssignedTo,
  onChangeAssigneeEmails,
  busy,
  error,
  onCancel,
  onSave,
  topInset = 0,
  bottomInset = 0,
}: AssignSheetProps) {
  const { t } = useTranslation();
  const tk = (k: string) => `${kind}.${k}`;
  const styles = makeStyles(theme);

  return (
    <PageSheet
      theme={theme}
      visible={visible}
      onRequestClose={onCancel}
      title={title}
      cancelLabel={t(tk("cancel"))}
      onCancel={onCancel}
      cancelDisabled={busy}
      action={{
        label: t(tk("save")),
        onPress: onSave,
        loading: busy,
        disabled: busy,
      }}
      topInset={topInset}
      bottomInset={bottomInset}
    >
      {/* Unlike EditMaxSheet's single field, the band can grow past one
       *  screen (a long roster, several pending emails) — it rides inside
       *  the composer's own scrollable meta pane there; here it has none,
       *  so this sheet supplies one. */}
      <ScrollView
        contentContainerStyle={styles.content}
        keyboardShouldPersistTaps="handled"
      >
        <ComposerAssigneesBand
          theme={theme}
          apiFetch={apiFetch}
          kind={kind}
          authoredAs={authoredAs}
          callerSub={callerSub}
          athleteRoster={athleteRoster}
          assignedTo={assignedTo}
          assigneeEmails={assigneeEmails}
          onChangeAssignedTo={onChangeAssignedTo}
          onChangeAssigneeEmails={onChangeAssigneeEmails}
        />
        {/* Never colour-only: this text IS the a11y cue, same convention as
         *  the band's own inline email-shape error. */}
        {error ? <Text style={styles.error}>{error}</Text> : null}
      </ScrollView>
    </PageSheet>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: t } = theme;
  return StyleSheet.create({
    content: { padding: s.s4, gap: s.s3 },
    error: { color: p.danger, fontSize: t.sizes.sm, fontWeight: "600" },
  });
}
